import React, { useState, useEffect } from 'react';
import { debounce } from 'lodash';
import { useMediaQuery } from 'react-responsive';
import { Helmet } from 'react-helmet-async';
import Faq from '../../components/Faq/Faq';
import BusDetailsList from '../../components/BusDetailsList/BusDetailsList';
import Footer from '../../components/Footer/Footer';
// import CompactList from '../../components/CompactList/CompactList';
import BreadCrumb from '../../components/BreadCrumb/BreadCrumb';
import PageHeader from '../../components/PageHeader/PageHeader';
import StarRatingBox from '../../components/StarRatingBox/StarRatingBox';
import DataLoaderError from '../../../server/errors/DataLoaderError';
import ExtraDescription from '../../components/ExtraDescription/ExtraDescription';
import BusModifySearch from '../../components/BusModifySearch/BusModifySearch';
import ThingsToKnow from '../../components/ThingsToKnow/ThingsToKnow';
// import MySafetyBanner from '../../components/MySafetyBanner/MySafetyBanner';
import AppContextProvider from '../../AppContext';
import ReturnBusSection from '../../components/ReturnBusSection';
import { RouteComponentProps } from 'react-router-dom';
import './RouteTemplate.css';
import { RouteTemplateData } from './RouteDataTypeChecker';
import {
  trackNewRouteEvent,
  trackRouteTemplateQueuedEvent,
} from '../../utils/omnitureTracking';
import { EnvironmentVariables } from '../../types';
import { getAppEnv } from '../../../config/env';
import AdsComponent from '../../components/AdsComponent';
import QuickInformationSection from '../../components/QuickInformation/QuickInformation';
import OfferDropdown from '../../components/OfferDropdown/OfferDropdown';
import { operatorShortNameMap } from '../../components/BusDetailsList/operatorShortNameMap';
import SchemaInformation from '../../components/SchemaInformation/SchemaInformation';
import CompactListV1 from '../../components/CompactList/CompactListV1';
import { yieldToMainThread } from '../../utils/scheduler';

type PathParamsType = {
  fromCityCode: string;
  toCityCode: string;
};

type MousePosType = {
  x: number;
  y: number;
};

type RouteTemplateProp = RouteComponentProps<PathParamsType> & {
  data: RouteTemplateData;
};
const env = getAppEnv();
const { DATA_LOADER_URL } = env.raw as EnvironmentVariables;
const isSSR = typeof window === 'undefined';

const RouteTemplate = ({ data }: RouteTemplateProp) => {
  const {
    id: routeId,
    templateData,
    topOperatorsList,
    sourceCityData,
    destinationCityData,
    topBusesFrom,
    returnRouteUrl,
    offers,
    description,
    mySafetyHtmls,
    famousRestaurantsNearBpDp,
    cityPlacesToVisit,
    quickInformation,
    seoOfferData,
    nearbyCitiesSource,
    nearbyCitiesDestination,
  } = data;
  const { name: sourceCityName } = sourceCityData;
  const { name: destinationCityName } = destinationCityData;
  const { code: sourceCityCode } = sourceCityData;
  const { code: destinationCityCode } = destinationCityData;
  // console.log(templateData.usps.length)
  const isMobileDevice = useMediaQuery({
    query: '(max-device-width: 768px)',
  });
  const [collapsableFlag, setcollapsableFlag] = useState(false);
  const [fromCity, setFromCity] = useState(
    sourceCityName && sourceCityCode ? sourceCityName : '',
  );
  const [toCity, setToCity] = useState(
    destinationCityName && destinationCityCode ? destinationCityName : '',
  );
  const [fromCode, setFromCode] = useState(
    sourceCityName && sourceCityCode ? sourceCityCode : '',
  );
  const [toCode, setToCode] = useState(
    destinationCityName && destinationCityCode ? destinationCityCode : '',
  );
  const [widget, setWidget] = useState<null | number>(null);
  const [funnelUrl, setFunnelUrl] = useState<'' | string>('');

  const date = new Date();
  date.setDate(date.getDate() + 1);

  const onBusClicked = (funnelUrl?: string) => {
    yieldToMainThread(() => {
      requestAnimationFrame(() => {
        onFromChange(sourceCityName, sourceCityCode);
        onToChange(destinationCityName, destinationCityCode);
        funnelUrl && setFunnelUrl(funnelUrl);
      });
    });
  };

  const trackRouteEvent = trackRouteTemplateQueuedEvent;

  const onFromChange = (city, code) => {
    setFromCity(city);
    setFromCode(code);
    trackRouteEvent(`Bus_SEO_Route_From_Select`);
  };

  const onToChange = (city, code) => {
    setToCity(city);
    setToCode(code);
    trackRouteEvent(`Bus_SEO_Route_To_Select`);
  };

  const getListingUrl = (): string => {
    return isMobileDevice
      ? `https://www.makemytrip.com/mbus/rnw/busListingPage?from=${fromCity}&from_code=${fromCode}&to=${toCity}&to_code=${toCode}&departure=travelDate&sort=Rating&`
      : `https://www.makemytrip.com/bus/search/${fromCity}/${toCity}/travelDate?from_city=${fromCode}&to_city=${toCode}&`;
  };

  useEffect(() => {
    const seeGuildLinesElement = document.getElementById('SeeGuidelines');
    seeGuildLinesElement &&
      seeGuildLinesElement.addEventListener('click', () => {
        trackRouteEvent(`Bus_SEO_RouteTemplate_MySafety_See_GuideLines_Click`);
      });

    const learnMoreElement = document.getElementById('LearnMore');
    learnMoreElement &&
      learnMoreElement.addEventListener('click', () => {
        trackRouteEvent(`Bus_SEO_RouteTemplate_MySafety_Learn_More_Click`);
      });
  }, []);

  useEffect(() => {
    let eventTrigger;

    if (routeId > 647356) {
      eventTrigger = setTimeout(() => {
        trackNewRouteEvent('Bus_SEO_Delta_Route', fromCity, toCity);
      }, 1000);
    }

    return () => {
      if (eventTrigger) {
        clearTimeout(eventTrigger);
      }
    };
  }, [routeId, fromCity, toCity]);

  const [mousePos, setMousePos] = useState({} as MousePosType);
  const [openOffersDropdown, setOpenOffersDropdown] = useState(false);
  const [hasDropdownOpened, setHasDropdownOpened] = useState(false);

  const busEvars = {
    m_v3: `${sourceCityName}-${destinationCityName}`,
    m_v75: 'SEO_Old',
    m_v100: 'SEO_Route_Template',
    m_v24: 'domestic bus',
  };

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.sessionStorage.setItem(
        'busPersistentEvars',
        JSON.stringify(busEvars),
      );
    }
  }, []);

  useEffect(() => {
    if (seoOfferData?.length > 0) {
      const handleMouseMove = debounce((event) => {
        if (!hasDropdownOpened) {
          setMousePos({ x: event.clientX, y: event.clientY });
        }
      }, 250);

      window.addEventListener('mousemove', handleMouseMove);

      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
      };
    }
  }, []);

  useEffect(() => {
    if (
      Object.keys(mousePos).length > 0 &&
      mousePos.y <= 20 &&
      !hasDropdownOpened
    ) {
      setOpenOffersDropdown(true);
      setHasDropdownOpened(true);
    }
  }, [mousePos]);

  const srcBpBlockData = {
    mainData:
      sourceCityData.cityBoardingPointsList &&
      sourceCityData?.cityBoardingPointsList?.length > 0 &&
      sourceCityData?.cityBoardingPointsList.map((it: string) => ({
        name: it,
        url: null,
      })),
    titlePrefix: 'Boarding Points',
    titleSuffix: ` in ${sourceCityName}`,
  };

  const srcOtherRoutesFromBlockData = {
    mainData:
      sourceCityData?.otherRoutesFrom?.length > 0 &&
      sourceCityData?.otherRoutesFrom,
    titlePrefix: 'Other routes',
    titleSuffix: ` from ${sourceCityName}`,
  };

  const srcOtherRoutesToBlockData = {
    mainData:
      sourceCityData?.otherRoutesTo?.length > 0 &&
      sourceCityData?.otherRoutesTo,
    titlePrefix: 'Other routes',
    titleSuffix: ` to ${sourceCityName}`,
  };

  const srcCabRoutesBlockData = {
    mainData: sourceCityData?.cabsRoutes,
    titlePrefix: 'Cabs',
    titleSuffix: ` from ${sourceCityName}`,
  };

  const operatorName = sourceCityData?.rtcRoutesSource?.operatorName;
  const shortOperatorName = operatorShortNameMap[operatorName]
    ? operatorShortNameMap[operatorName]
    : operatorName;

  const rtcRoutesSourceBlockData = {
    mainData:
      sourceCityData?.rtcRoutesSource?.rtcRoutes &&
      sourceCityData?.rtcRoutesSource?.rtcRoutes?.length > 0 &&
      sourceCityData.rtcRoutesSource?.rtcRoutes?.map((it) => ({
        name: it.name,
        url: it.url,
      })),
    titlePrefix: `Popular ${shortOperatorName} Routes`,
    titleSuffix: ` from ${sourceCityName}`,
  };

  const srcRailsRoutesBlockData = {
    mainData: sourceCityData.railsRoutes,
    titlePrefix: 'Trains',
    titleSuffix: ` from ${sourceCityName}`,
  };

  const srcVolvoACRoutes = {
    mainData: sourceCityData.volvoACRoutes,
    titlePrefix: 'Other Volvo AC Buses',
    titleSuffix: ` from ${sourceCityName}`,
  };

  const srcHotelsList = {
    mainData: sourceCityData.cityHotels,
    titlePrefix: 'Hotels',
    titleSuffix: ` in ${sourceCityName}`,
  };

  const srcHolidaysList = {
    mainData: sourceCityData.cityHolidays,
    titlePrefix: 'Holiday Packages',
    titleSuffix: ` in ${sourceCityName}`,
  };

  const sourcePopularCities = {
    mainData: nearbyCitiesSource,
    titlePrefix: `Popular Cities near`,
    titleSuffix: ` ${sourceCityName}`,
  };

  const sourceInterlinkingBlockData = [
    srcBpBlockData,
    srcOtherRoutesFromBlockData,
    srcOtherRoutesToBlockData,
    srcVolvoACRoutes,
    srcCabRoutesBlockData,
    rtcRoutesSourceBlockData,
    srcRailsRoutesBlockData,
    srcHotelsList,
    srcHolidaysList,
    sourcePopularCities,
  ];

  const destBpBlockData = {
    mainData:
      destinationCityData.cityDropPointsList &&
      destinationCityData?.cityDropPointsList?.length > 0 &&
      destinationCityData?.cityDropPointsList.map((it: string) => ({
        name: it,
        url: null,
      })),
    titlePrefix: 'Boarding Points',
    titleSuffix: ` in ${destinationCityName}`,
  };

  const destOtherRoutesFromBlockData = {
    mainData:
      destinationCityData?.otherRoutesFrom?.length > 0 &&
      destinationCityData?.otherRoutesFrom,
    titlePrefix: 'Other routes',
    titleSuffix: ` from ${destinationCityName}`,
  };

  const destOtherRoutesToBlockData = {
    mainData:
      destinationCityData?.otherRoutesTo?.length > 0 &&
      destinationCityData?.otherRoutesTo,
    titlePrefix: 'Other routes',
    titleSuffix: ` to ${destinationCityName}`,
  };

  const destCabRoutesBlockData = {
    mainData: destinationCityData?.cabsRoutes,
    titlePrefix: 'Cabs',
    titleSuffix: ` from ${destinationCityName}`,
  };

  const destOperatorName = destinationCityData?.rtcRoutesSource?.operatorName;
  const destShortOperatorName = operatorShortNameMap[destOperatorName]
    ? operatorShortNameMap[operatorName]
    : operatorName;

  const rtcRoutesDestinationBlockData = {
    mainData:
      destinationCityData?.rtcRoutesSource?.rtcRoutes &&
      destinationCityData?.rtcRoutesSource?.rtcRoutes?.length > 0 &&
      destinationCityData.rtcRoutesSource?.rtcRoutes?.map((it) => ({
        name: it.name,
        url: it.url,
      })),
    titlePrefix: `Popular ${destShortOperatorName} Routes`,
    titleSuffix: ` from ${destinationCityName}`,
  };

  const destRailsRoutesBlockData = {
    mainData: destinationCityData.railsRoutes,
    titlePrefix: 'Trains',
    titleSuffix: ` from ${destinationCityName}`,
  };

  const destVolvoACRoutes = {
    mainData: destinationCityData.volvoACRoutes,
    titlePrefix: 'Other Volvo AC Buses',
    titleSuffix: ` from ${destinationCityName}`,
  };

  const destHotelsList = {
    mainData: destinationCityData.cityHotels,
    titlePrefix: 'Hotels',
    titleSuffix: ` in ${destinationCityName}`,
  };

  const destHolidaysList = {
    mainData: destinationCityData.cityHolidays,
    titlePrefix: 'Holiday Packages',
    titleSuffix: ` in ${destinationCityName}`,
  };

  const destinationPopularCities = {
    mainData: nearbyCitiesDestination,
    titlePrefix: `Popular Cities near`,
    titleSuffix: ` ${destinationCityName}`,
  };

  const destinationInterlinkingBlockData = [
    destBpBlockData,
    destOtherRoutesFromBlockData,
    destOtherRoutesToBlockData,
    destVolvoACRoutes,
    destCabRoutesBlockData,
    rtcRoutesDestinationBlockData,
    destRailsRoutesBlockData,
    destHotelsList,
    destHolidaysList,
    destinationPopularCities,
  ];

  return (
    <AppContextProvider
      omniUrlArgs={'source=SEO_Route_Template'}
      trackTemplateEvent={trackRouteEvent}
    >
      <SchemaInformation
        schemaInformation={quickInformation}
        templateData={templateData}
      />

      <div className={`busSeoPage`}>
        <Helmet>
          <title>{templateData.metaTitle}</title>
          <meta name="description" content={templateData.metaDescription} />
          <meta name="keywords" content={templateData.metaKeywords} />
          <link rel="canonical" href={templateData.sampleUrl} />
        </Helmet>
        <div className={`desktopShow makeSticky`}>
          <BusModifySearch
            funnelUrl={funnelUrl}
            sourceCityName={sourceCityName}
            destinationCityName={destinationCityName}
            widget={widget}
            setWidget={setWidget}
            onFromChange={onFromChange}
            onToChange={onToChange}
            fromCity={fromCity}
            toCity={toCity}
            fromCode={fromCode}
            toCode={toCode}
            setcollapsableFlag={setcollapsableFlag}
            collapsableFlag={collapsableFlag}
            isMobileDevice={false}
            demoLink={true}
          />
        </div>
        <div className={`mobileShow makeSticky`}>
          <BusModifySearch
            funnelUrl={funnelUrl}
            sourceCityName={sourceCityName}
            destinationCityName={destinationCityName}
            widget={widget}
            setWidget={setWidget}
            onFromChange={onFromChange}
            onToChange={onToChange}
            fromCity={fromCity}
            toCity={toCity}
            fromCode={fromCode}
            toCode={toCode}
            setcollapsableFlag={setcollapsableFlag}
            collapsableFlag={collapsableFlag}
            isMobileDevice={true}
            demoLink={true}
          />
        </div>
        <main className="container">
          <BreadCrumb breadcrumb={templateData.breadcrumb} type={`_Route`} />
          <PageHeader title={templateData.h1Title} />
          {templateData.extraInfo && (
            <div className="pageExtraInfo font15 appendTop20 appendBottom15">
              {templateData.extraInfo}
            </div>
          )}

          {
            <AdsComponent
              template={'Route'}
              placedAt={'top'}
              isMobileDevice={isMobileDevice}
              desktopAdConfig={{
                position: templateData.bannerDesktop
                  ? templateData.bannerDesktop
                  : 'none',
                class: 'desktopShow appendBottom20 adsLayout',
                style: { marginTop: null },
              }}
              mobileAdConfig={{
                position: templateData.bannerMobile
                  ? templateData.bannerMobile
                  : 'none',
                class: 'mobileShow appendBottom20 adsLayout',
                style: { marginTop: null },
              }}
            />
          }
          <br style={{ display: 'none' }} />
          {topBusesFrom &&
            topBusesFrom.busGroups &&
            topBusesFrom.busGroups.length > 0 && (
              <>
                <BusDetailsList
                  noOfListings={
                    templateData.noOfListings ? templateData.noOfListings : 10
                  }
                  busGroups={topBusesFrom.busGroups}
                  type={'_Route'}
                  titlePrefix={'Top Buses '}
                  route={`from ${sourceCityName} to ${destinationCityName}`}
                  onBusClicked={onBusClicked}
                  setcollapsableFlag={setcollapsableFlag}
                  setWidget={setWidget}
                  isMobileDevice={isMobileDevice}
                  fromCity={fromCity}
                  toCity={toCity}
                  fromCode={fromCode}
                  toCode={toCode}
                  selectedDay={date}
                />
                <br />
              </>
            )}
          {returnRouteUrl && (
            <ReturnBusSection
              type={'Route'}
              sourceCityName={sourceCityName}
              destinationCityName={destinationCityName}
              url={returnRouteUrl}
            />
          )}
          {
            <AdsComponent
              template={'Route'}
              placedAt={'middle'}
              isMobileDevice={isMobileDevice}
              desktopAdConfig={{
                position: templateData.bannerDesktop
                  ? templateData.bannerDesktop
                  : 'none',
                class: 'desktopShow appendBottom20 adsLayout',
                style: { marginTop: '-20px' },
              }}
              mobileAdConfig={{
                position: templateData.bannerMobile
                  ? templateData.bannerMobile
                  : 'none',
                class: 'mobileShow appendBottom10 adsLayout',
                style: { marginTop: '-5px' },
              }}
            />
          }
          {topOperatorsList && topOperatorsList.length > 0 && (
            <section className="visibilityAuto">
              <h2
                style={{ display: 'none' }} // disabled in SSR HTML
                className="starRatngWrap__top--mob paddingR16"
              >
                <span
                  style={{ display: 'block' }}
                  className="font22 latoLight appendBottom2"
                >
                  Top Operators
                </span>
                <span className="font16 latoBold lineHeight20 blackText appendBottom5">
                  {`for ${sourceCityName} to ${destinationCityName}`}
                </span>
              </h2>
              <h2 className="starRatngWrap__top--desk">
                <span>
                  {`Top Operators for ${sourceCityName} to ${destinationCityName}`}
                </span>
              </h2>
              <StarRatingBox topOperatorsList={topOperatorsList} />
            </section>
          )}

          <div className={`busSeoWrap__Details makeFlex visibilityAuto`}>
            {sourceInterlinkingBlockData.map((item, idx) => {
              const { mainData, titlePrefix, titleSuffix } = item;
              if (!mainData || mainData?.length < 1) {
                return;
              } else {
                return (
                  <CompactListV1
                    blockData={mainData}
                    titlePrefix={titlePrefix}
                    titleSuffix={titleSuffix}
                    key={idx}
                  />
                );
              }
            })}
          </div>

          <QuickInformationSection quickInformation={quickInformation} />
          <ExtraDescription content={templateData.descriptionInHtml} />
          {/*description data(can be added via bus-seo-admin UI)*/}
          {description && (
            <section>
              <ThingsToKnow text={description} title={``} cityName={''} />
            </section>
          )}
          {sourceCityData.description && (
            <section>
              <ThingsToKnow
                text={sourceCityData.description}
                title={`Things To Know About`}
                cityName={sourceCityName}
              />
            </section>
          )}

          <div className={`busSeoWrap__Details makeFlex visibilityAuto`}>
            {destinationInterlinkingBlockData.map((item, idx) => {
              const { mainData, titlePrefix, titleSuffix } = item;
              if (!mainData || mainData?.length < 1) {
                return;
              } else {
                return (
                  <CompactListV1
                    blockData={mainData}
                    titlePrefix={titlePrefix}
                    titleSuffix={titleSuffix}
                    key={idx}
                  />
                );
              }
            })}
          </div>

          {destinationCityData.description && (
            <section>
              <ThingsToKnow
                text={destinationCityData.description}
                title={`Things To Know About`}
                cityName={destinationCityName}
              />
            </section>
          )}

          {cityPlacesToVisit && (
            <section className="visibilityAuto">
              <ThingsToKnow
                text={cityPlacesToVisit}
                title={`Places To Visit`}
                cityName={''}
              />
            </section>
          )}
          {famousRestaurantsNearBpDp && (
            <section className="visibilityAuto">
              <ThingsToKnow
                text={famousRestaurantsNearBpDp}
                title={`Famous Restaurants To Visit`}
                cityName={''}
              />
            </section>
          )}
          {templateData && templateData.faq && templateData.faq.length > 0 && (
            <section className="visibilityAuto">
              <Faq
                data={templateData.faq}
                FaqTitle={`FAQ - ${sourceCityName} to ${destinationCityName} Bus Ticket Booking`}
                getListingUrl={getListingUrl}
                isMobileDevice={isMobileDevice}
                pageUrl={templateData.sampleUrl}
                type={'_Route'}
              />
            </section>
          )}
        </main>
        {!isMobileDevice &&
          openOffersDropdown &&
          hasDropdownOpened &&
          seoOfferData.length > 0 && (
            <OfferDropdown
              seoOfferData={seoOfferData}
              setOpenOffersDropdown={setOpenOffersDropdown}
            />
          )}
        {templateData && <Footer template={templateData} />}
        <div
          className="blackOverlay"
          onClick={() => {
            setcollapsableFlag(true);
          }}
        ></div>
      </div>
    </AppContextProvider>
  );
};

RouteTemplate.fetchData = async (_, match, urlObj, gRPCClient) => {
  const {
    params: { fromCityCode, toCityCode },
  } = match;
  const url = `${DATA_LOADER_URL}/api/routeTemplateData/${fromCityCode}/${toCityCode}?${urlObj.query}`;
  let responseData;

  const response = await fetch(url);
  const textResponse = await response.text();
  try {
    responseData = JSON.parse(textResponse);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('url', url);
    throw new Error('JSON parse exception: ' + textResponse);
  }
  if (responseData.status !== 'SUCCESS') {
    // eslint-disable-next-line no-console
    console.error('url', url);
    throw new DataLoaderError(
      `Unable to fetch city details of ${fromCityCode}-${toCityCode}`,
      responseData.error,
    );
  }
  return responseData;
  // return await gRPCClient.getBusCityTemplateDate(cityCode, 'dynamic');
};
export default RouteTemplate;
